export interface EmbeddedAuthConfig {
  domain: string;
  clientId: string;
  audience: string;
  scope: string;
  apiBaseUrl: string;
}

export type TokenSource = 'passwordless' | 'enterprise' | 'external';

export interface TokenSet {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
  scope?: string;
  source: TokenSource;
}

export interface PkcePair {
  verifier: string;
  challenge: string;
}

export class AuthApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

const authDomain = (config: EmbeddedAuthConfig) => {
  const domain = config.domain.replace(/\/+$/, '');
  return /^https?:\/\//.test(domain) ? domain : `https://${domain}`;
};

const apiBase = (config: EmbeddedAuthConfig) =>
  config.apiBaseUrl.replace(/\/+$/, '');

async function parseAuth0Error(response: Response): Promise<AuthApiError> {
  let code = 'request_error';
  let description = response.statusText;
  try {
    const body = await response.json();
    code = body.error || body.code || code;
    description = body.error_description || body.description || description;
  } catch {
    // no JSON body
  }
  return new AuthApiError(code, description, response.status);
}

async function parseApiError(response: Response): Promise<AuthApiError> {
  let description = response.statusText;
  try {
    const body = await response.json();
    description = body.message || description;
  } catch {
    // no JSON body
  }
  return new AuthApiError('request_error', description, response.status);
}

const fromApiTokens = (raw: {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn: number;
  scope?: string;
}): TokenSet => ({
  accessToken: raw.accessToken,
  refreshToken: raw.refreshToken,
  idToken: raw.idToken,
  expiresAt: Date.now() + raw.expiresIn * 1000,
  scope: raw.scope,
  source: 'passwordless',
});

const fromAuth0Tokens = (raw: {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  scope?: string;
}): TokenSet => ({
  accessToken: raw.access_token,
  refreshToken: raw.refresh_token,
  idToken: raw.id_token,
  expiresAt: Date.now() + raw.expires_in * 1000,
  scope: raw.scope,
  source: 'enterprise',
});

export function randomString(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function createPkcePair(): Promise<PkcePair> {
  const verifier = randomString(48);
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  return { verifier, challenge: base64UrlEncode(digest) };
}

export async function startPasswordless(
  config: EmbeddedAuthConfig,
  email: string
): Promise<void> {
  const response = await fetch(`${apiBase(config)}/auth/passwordless/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
}

export async function verifyPasswordlessOtp(
  config: EmbeddedAuthConfig,
  email: string,
  otp: string
): Promise<TokenSet> {
  const response = await fetch(`${apiBase(config)}/auth/passwordless/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
  return fromApiTokens(await response.json());
}

export async function refreshPasswordlessTokens(
  config: EmbeddedAuthConfig,
  refreshToken: string
): Promise<TokenSet> {
  const response = await fetch(`${apiBase(config)}/auth/passwordless/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
  const next = fromApiTokens(await response.json());
  if (!next.refreshToken) {
    next.refreshToken = refreshToken;
  }
  return next;
}

export function buildAuthorizeUrl(
  config: EmbeddedAuthConfig,
  params: {
    connection: string;
    redirectUri: string;
    state: string;
    nonce: string;
    codeChallenge: string;
  }
): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: params.redirectUri,
    scope: config.scope,
    audience: config.audience,
    connection: params.connection,
    state: params.state,
    nonce: params.nonce,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
  });
  return `${authDomain(config)}/authorize?${query.toString()}`;
}

export async function exchangeAuthorizationCode(
  config: EmbeddedAuthConfig,
  params: { code: string; redirectUri: string; codeVerifier: string }
): Promise<TokenSet> {
  const response = await fetch(`${authDomain(config)}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code: params.code,
      redirect_uri: params.redirectUri,
      code_verifier: params.codeVerifier,
    }),
  });
  if (!response.ok) {
    throw await parseAuth0Error(response);
  }
  return fromAuth0Tokens(await response.json());
}

export async function refreshEnterpriseTokens(
  config: EmbeddedAuthConfig,
  refreshToken: string
): Promise<TokenSet> {
  const response = await fetch(`${authDomain(config)}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!response.ok) {
    throw await parseAuth0Error(response);
  }
  const next = fromAuth0Tokens(await response.json());
  if (!next.refreshToken) {
    next.refreshToken = refreshToken;
  }
  return next;
}

export function buildLogoutUrl(
  config: EmbeddedAuthConfig,
  returnTo: string
): string {
  const query = new URLSearchParams({
    client_id: config.clientId,
    returnTo,
  });
  return `${authDomain(config)}/v2/logout?${query.toString()}`;
}
